import practice from '../../backend/pkg/puzzle/testdata/tutorial.json'
import type { Puzzle } from './scoring'
import { percentOfTop, placing } from './share'

/**
 * The practice round: CAT is on the board and the S goes on the 2W square at
 * its end to make CATS. The puzzle and its solution (every legal play, tallied
 * by score) are shared with the Go solver, whose tests keep them in step.
 * Nothing here is saved or sent anywhere.
 */
export const TUTORIAL_PUZZLE = practice as Puzzle

/** The S in the rack and the square it goes on. */
export const TUTORIAL_TILE = TUTORIAL_PUZZLE.player_letters.indexOf('S')
export const TUTORIAL_SQUARE = { row: 2, col: 4 }

export interface TutorialStep {
  title: string
  body: string
  /** Label of the button that moves on; steps without one move on when the player does the thing. */
  next?: string
  /** What to highlight. */
  coach?: { rackIndex?: number; cell?: string }
}

export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    title: 'Welcome to wordevery.day',
    body: 'Every day, everyone gets the same board and the same seven letters. Find the highest-scoring play: your tiles go in one row or column, connect to the board, and every word they make must be real. This practice round takes a minute.',
    next: 'Start',
  },
  {
    title: 'Pick a letter',
    body: 'Your letters are along the bottom. Tap the glowing S.',
    coach: { rackIndex: TUTORIAL_TILE },
  },
  {
    title: 'Place it',
    body: 'Now tap the glowing square to turn CAT into CATS. You can also drag letters straight onto the board.',
    coach: { cell: `${TUTORIAL_SQUARE.row},${TUTORIAL_SQUARE.col}` },
  },
  {
    title: 'Scoring',
    body: 'CATS scores 12: its letters are worth 3 + 1 + 1 + 1 and the 2W square doubles the word. 2L and 3L multiply one letter. Bonuses only count for tiles you place, and using all seven letters adds 50.',
    next: 'Next',
  },
  {
    title: 'Can you beat 12?',
    body: 'CATS isn\'t the best play here. Try other words with your letters: longer words and bonus squares score more, and one tile can make two words at once. Your best placement is kept under "Best so far"; tap it to bring it back.',
    next: "I'm ready to play",
  },
  {
    title: 'One play a day',
    body: "You only get one play, so explore before committing; you won't see the top score until you've played. Tap Play, then Confirm.",
  },
  {
    // Filled in from the player's play by tutorialFeedback.
    title: 'How you did',
    body: '',
  },
]

/** The bits of game state the tutorial watches. */
export interface TutorialView {
  selected: number | null
  placed: { row: number; col: number; rackIndex: number }[]
  valid: boolean
  finished: boolean
}

/** Moves the tutorial on when the player has done what the current step asks; steps with a button only move on through it. */
export function advance(step: number, s: TutorialView): number {
  const onSquare = s.placed.some(
    (p) => p.rackIndex === TUTORIAL_TILE && p.row === TUTORIAL_SQUARE.row && p.col === TUTORIAL_SQUARE.col,
  )
  switch (step) {
    case 1:
      return s.selected === TUTORIAL_TILE || onSquare ? advance(2, s) : 1
    case 2:
      return onSquare && s.valid ? 3 : 2
    case 5:
      return s.finished ? 6 : 5
    default:
      return step
  }
}

/** The final step: how the practice play ranked among every legal play. */
export function tutorialFeedback(score: number, words: string[]): { title: string; body: string } {
  const top = TUTORIAL_PUZZLE.best_play!
  const place = placing(TUTORIAL_PUZZLE, score)!
  const pct = percentOfTop(score, top.score)
  const title = place.rank === 1 ? 'Perfect: the top play!' : pct >= 70 ? 'Great play!' : 'Nice first play!'
  const result = `${words.join(' + ')} scored ${score}: #${place.rank} of ${place.total} possible plays, ${pct}% of the top score.`
  const topPlay =
    place.rank === 1
      ? ' Nobody can beat that.'
      : ` The top play was ${top.words.join(' + ')} for ${top.score}; tap "Show top play" to see it.`
  return {
    title,
    body: `${result}${topPlay} In the daily puzzle you also see how everyone else did. Today’s puzzle is waiting!`,
  }
}
